import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import PreviewIcon from '@mui/icons-material/Preview';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  listEmailTemplates,
  previewEmail,
  sendTestEmail,
  type EmailTestParam,
  type EmailTestTemplate,
} from '../services/emailsAdminApi';
import { ContentPageLayout } from '../components/Shared';

const STORAGE_KEY = 'admin-email-test-recipient';

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Authentication',
  subscription: 'Subscriptions',
  messaging: 'Messaging',
  social: 'Social & teachers',
  progression: 'Progression',
};

function getCategoryChipSx(category: string) {
  return (theme: Theme) => {
    const paletteKey =
      category === 'auth'
        ? 'primary'
        : category === 'subscription'
          ? 'success'
          : category === 'messaging'
            ? 'info'
            : category === 'social'
              ? 'secondary'
              : category === 'progression'
                ? 'warning'
                : 'primary';

    const main =
      paletteKey === 'primary'
        ? theme.palette.primary.main
        : theme.palette[paletteKey]?.main || theme.palette.primary.main;

    return {
      bgcolor: alpha(main, 0.12),
      color: main,
      border: `1px solid ${alpha(main, 0.28)}`,
      fontWeight: 600,
    };
  };
}

function paramToFieldValue(param: EmailTestParam, params: Record<string, string>): string {
  const raw = params[param.key];
  if (raw !== undefined) return raw;
  if (param.type === 'json' && param.default != null) {
    return typeof param.default === 'string' ? param.default : JSON.stringify(param.default, null, 2);
  }
  if (param.default != null) return String(param.default);
  return '';
}

function buildPayloadParams(
  template: EmailTestTemplate,
  params: Record<string, string>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const param of template.params) {
    const raw = params[param.key];
    if (raw === undefined || raw === '') continue;
    if (param.type === 'number') {
      payload[param.key] = Number(raw);
    } else if (param.type === 'boolean') {
      payload[param.key] = raw === 'true';
    } else if (param.type === 'json') {
      payload[param.key] = JSON.parse(raw);
    } else {
      payload[param.key] = raw;
    }
  }
  return payload;
}

function defaultParamsForTemplate(template: EmailTestTemplate): Record<string, string> {
  const defaults: Record<string, string> = {};
  for (const param of template.params) {
    defaults[param.key] = paramToFieldValue(param, {});
  }
  return defaults;
}

const EmailsAdminPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTestTemplate[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [testRecipient, setTestRecipient] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [language, setLanguage] = useState<'en' | 'fr'>('fr');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [selectedTemplate, setSelectedTemplate] = useState<EmailTestTemplate | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listEmailTemplates();
      setTemplates(res.data.templates);
      setCategories(res.data.categories);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, testRecipient);
  }, [testRecipient]);

  const filteredTemplates = useMemo(() => {
    if (!categoryFilter) return templates;
    return templates.filter((t) => t.category === categoryFilter);
  }, [templates, categoryFilter]);

  const groupedTemplates = useMemo(() => {
    const groups = new Map<string, EmailTestTemplate[]>();
    for (const template of filteredTemplates) {
      const list = groups.get(template.category) || [];
      list.push(template);
      groups.set(template.category, list);
    }
    return groups;
  }, [filteredTemplates]);

  const openTemplateDialog = (template: EmailTestTemplate) => {
    setSelectedTemplate(template);
    setParams(defaultParamsForTemplate(template));
    setPreviewHtml(null);
    setPreviewSubject(null);
    setSuccess(null);
    setError(null);
  };

  const closeTemplateDialog = () => {
    setSelectedTemplate(null);
    setPreviewHtml(null);
    setPreviewSubject(null);
  };

  const handlePreview = async () => {
    if (!selectedTemplate) return;
    setWorking(true);
    setError(null);
    try {
      const payloadParams = buildPayloadParams(selectedTemplate, params);
      const res = await previewEmail({
        templateId: selectedTemplate.id,
        language,
        params: payloadParams,
      });
      setPreviewSubject(res.data.subject);
      setPreviewHtml(res.data.html);
      setPreviewOpen(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setWorking(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;
    if (!testRecipient.trim()) {
      setError('Enter a test recipient email address at the top of the page.');
      return;
    }
    setWorking(true);
    setError(null);
    setSuccess(null);
    try {
      const payloadParams = buildPayloadParams(selectedTemplate, params);
      const res = await sendTestEmail({
        templateId: selectedTemplate.id,
        recipient: testRecipient.trim(),
        language,
        params: payloadParams,
      });
      setSuccess(`Sent "${res.data.subject}" to ${res.data.recipient}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setWorking(false);
    }
  };

  const renderParamField = (param: EmailTestParam) => {
    if (param.type === 'boolean') {
      return (
        <FormControlLabel
          key={param.key}
          control={
            <Switch
              checked={params[param.key] === 'true'}
              onChange={(e) =>
                setParams((prev) => ({ ...prev, [param.key]: e.target.checked ? 'true' : 'false' }))
              }
            />
          }
          label={param.label}
        />
      );
    }

    return (
      <TextField
        key={param.key}
        fullWidth
        size="small"
        margin="normal"
        label={param.label}
        type={param.type === 'number' ? 'number' : 'text'}
        value={params[param.key] ?? ''}
        onChange={(e) => setParams((prev) => ({ ...prev, [param.key]: e.target.value }))}
        multiline={param.type === 'json'}
        minRows={param.type === 'json' ? 3 : 1}
        helperText={param.type === 'json' ? 'JSON value' : undefined}
      />
    );
  };

  return (
    <ContentPageLayout>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography variant="h5" color="primary" fontWeight="bold">
              Email testing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preview and send test copies of every platform email template.
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={loadTemplates} disabled={loading}>
            Refresh
          </Button>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Test recipient email"
                type="email"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                placeholder="you@example.com"
                helperText="All test emails are sent to this address with a [TEST] subject prefix."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  label="Language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'en' | 'fr')}
                >
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {CATEGORY_LABELS[cat] || cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3}>
            {[...groupedTemplates.entries()].map(([category, items]) => (
              <Box key={category}>
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                  {CATEGORY_LABELS[category] || category}
                  <Chip
                    size="small"
                    label={items.length}
                    sx={(theme) => ({ ml: 1, ...getCategoryChipSx(category)(theme) })}
                  />
                </Typography>
                <Grid container spacing={2}>
                  {items.map((template) => (
                    <Grid item xs={12} md={6} lg={4} key={template.id}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {template.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={CATEGORY_LABELS[template.category] || template.category}
                            sx={getCategoryChipSx(template.category)}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {template.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {template.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                          {template.params.length} configurable parameter{template.params.length === 1 ? '' : 's'}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EmailIcon />}
                          onClick={() => openTemplateDialog(template)}
                        >
                          Configure & send
                        </Button>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        <Dialog open={Boolean(selectedTemplate)} onClose={closeTemplateDialog} maxWidth="md" fullWidth>
          <DialogTitle>{selectedTemplate?.name}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedTemplate?.description}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Template: {selectedTemplate?.id}
            </Typography>
            {selectedTemplate?.params.map(renderParamField)}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTemplateDialog}>Close</Button>
            <Button
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              disabled={working}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={working || !testRecipient.trim()}
            >
              {working ? 'Sending…' : 'Send test email'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Preview: {previewSubject}
          </DialogTitle>
          <DialogContent dividers>
            {previewHtml ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '70vh',
                  bgcolor: '#fff',
                }}
              >
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  style={{ width: '100%', minHeight: 480, border: 'none' }}
                />
              </Box>
            ) : (
              <CircularProgress size={24} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ContentPageLayout>
  );
};

export default EmailsAdminPage;
